import React, { useEffect, useState } from 'react'
import {
    Container,
    Stack,
    Typography,
    Button,
    TextField,
    Select,
    InputLabel,
    MenuItem,
    Autocomplete,
    FormControl,
} from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment';
import api from '../../api';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Vehicle types are stored on the server as integers. Adjust the labels here
// if the backend uses a different mapping.
export const VEHICLE_TYPES = [
    { label: 'Two Wheeler', value: 0 },
    { label: 'Three Wheeler', value: 1 },
    { label: 'Tempo / Pickup', value: 2 },
    { label: 'Mini Truck', value: 3 },
    { label: 'Truck', value: 4 },
    { label: 'Van', value: 5 },
];

// `value` is sent when creating a vehicle (numeric status), `key` is used by the
// search/filter endpoint (string status e.g. "available").
export const VEHICLE_STATUS = [
    { label: 'Available', value: 0, key: 'available' },
    { label: 'On Trip', value: 1, key: 'on_trip' },
    { label: 'Maintenance', value: 2, key: 'maintenance' },
    { label: 'Inactive', value: 3, key: 'inactive' },
];

const INITIAL_VEHICLE = {
    registrationNumber: '',
    vehicleType: '',
    make: '',
    model: '',
    color: '',
    capacityKg: '',
    capacityVolume: '',
    driverId: '',
    status: 0,
    insuranceExpiry: '',
    fitnessExpiry: '',
    pollutionExpiry: '',
    notes: '',
};

const INITIAL_ERRORS = {
    registrationNumber: '',
    vehicleType: '',
    make: '',
    model: '',
    capacityKg: '',
};

const REGISTRATION_PATTERN = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/;

const validateField = (name, value) => {
    switch (name) {
        case 'registrationNumber':
            return REGISTRATION_PATTERN.test(String(value).toUpperCase())
                ? ''
                : 'Enter a valid registration number (e.g. UP53AB1234)';
        case 'make':
        case 'model':
            return String(value).trim().length > 1 ? '' : 'Minimum two characters required!';
        case 'capacityKg':
            return parseFloat(value) > 0 ? '' : 'Enter a valid capacity in kg';
        case 'vehicleType':
            return value === '' || value === null || value === undefined ? 'Select a vehicle type' : '';
        default:
            return '';
    }
};

export default function AddVehicleComponent() {
    const { state } = useLocation();
    const navigate = useNavigate();

    const [showVehicle, setVehicle] = useState(INITIAL_VEHICLE);
    const [showErrors, setErrors] = useState(INITIAL_ERRORS);
    const [drivers, setDrivers] = useState([]);
    const [showEditButton, setShowEditButton] = useState(false);

    useEffect(() => {
        getDrivers();
    }, []);

    useEffect(() => {
        if (state && state.showEditVehicle) {
            const edit = state.showEditVehicle;
            setVehicle({
                registrationNumber: edit.registrationNumber || '',
                vehicleType: edit.vehicleType ?? '',
                make: edit.make || '',
                model: edit.model || '',
                color: edit.color || '',
                capacityKg: edit.capacityKg ?? '',
                capacityVolume: edit.capacityVolume ?? '',
                driverId: edit.driverId ?? (edit.driver && edit.driver.id) ?? '',
                status: edit.status ?? 0,
                insuranceExpiry: edit.insuranceExpiry || '',
                fitnessExpiry: edit.fitnessExpiry || '',
                pollutionExpiry: edit.pollutionExpiry || '',
                notes: edit.notes || '',
                id: edit.id,
            });
            setShowEditButton(true);
        }
    }, [state]);

    const getDrivers = async () => {
        try {
            const response = await api.post('/api/v1/employees/allEmployees', {
                page: 1,
                query: { employeeType: 'Delivery Person' },
            });
            if (response.data.success && Array.isArray(response.data.employees)) {
                const options = response.data.employees
                    .filter((employee) => employee && employee.user)
                    .map((employee) => ({
                        label: `${employee.user.name} (${employee.phoneNumber || employee.user.email || ''})`,
                        value: employee.user.id,
                    }));
                setDrivers(options);
            }
        } catch (error) {
            toast.error('Could not load drivers');
        }
    };

    const onChangeVehicle = (event) => {
        const { name } = event.target;
        let { value } = event.target;
        if (name === 'registrationNumber') {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        }
        setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
        setVehicle((prev) => ({ ...prev, [name]: value }));
    };

    const onChangeDriver = (event, option) => {
        setVehicle((prev) => ({ ...prev, driverId: option ? option.value : '' }));
    };

    const buildPayload = () => ({
        registrationNumber: String(showVehicle.registrationNumber).toUpperCase().trim(),
        vehicleType: showVehicle.vehicleType === '' ? null : Number(showVehicle.vehicleType),
        make: showVehicle.make.trim(),
        model: showVehicle.model.trim(),
        color: showVehicle.color.trim(),
        capacityKg: showVehicle.capacityKg === '' ? null : Number(showVehicle.capacityKg),
        capacityVolume: showVehicle.capacityVolume === '' ? null : Number(showVehicle.capacityVolume),
        driverId: showVehicle.driverId === '' ? null : Number(showVehicle.driverId),
        status: Number(showVehicle.status),
        insuranceExpiry: showVehicle.insuranceExpiry || null,
        fitnessExpiry: showVehicle.fitnessExpiry || null,
        pollutionExpiry: showVehicle.pollutionExpiry || null,
        notes: showVehicle.notes.trim(),
    });

    const isFormValid = () => {
        const errors = {
            registrationNumber: validateField('registrationNumber', showVehicle.registrationNumber),
            vehicleType: validateField('vehicleType', showVehicle.vehicleType),
            make: validateField('make', showVehicle.make),
            model: validateField('model', showVehicle.model),
            capacityKg: validateField('capacityKg', showVehicle.capacityKg),
        };
        setErrors(errors);
        return !Object.values(errors).some((message) => message.length > 0);
    };

    const onClickSave = () => {
        if (!isFormValid()) {
            toast.error('Please fix the highlighted fields');
            return;
        }

        api
            .post('/api/v1/vehicles', { vehicle: buildPayload() })
            .then((response) => {
                if (response.data.success) {
                    toast.success(response.data.message || 'Vehicle added successfully');
                    navigate('/vehicles');
                } else {
                    toast.error(response.data.message);
                }
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || 'Something went wrong!');
            });
    };

    const onClickEdit = () => {
        if (!isFormValid()) {
            toast.error('Please fix the highlighted fields');
            return;
        }

        api
            .put('/api/v1/vehicles/update', { vehicle: { ...buildPayload(), id: showVehicle.id } })
            .then((response) => {
                if (response.data.success) {
                    toast.success(response.data.message || 'Vehicle updated successfully');
                    navigate('/vehicles');
                } else {
                    toast.error(response.data.message);
                }
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || 'Something went wrong!');
            });
    };

    const onClickCancel = () => {
        setVehicle(INITIAL_VEHICLE);
        setErrors(INITIAL_ERRORS);
    };

    const selectedDriver = drivers.find((driver) => driver.value === showVehicle.driverId) || null;

    return (
        <>
            <Container>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        {showEditButton ? 'Edit Vehicle' : 'Add Vehicle'}
                    </Typography>
                    <Button variant="contained" onClick={onClickCancel}>
                        New Vehicle
                    </Button>
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Registration Number"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="registrationNumber"
                        value={showVehicle.registrationNumber}
                        onChange={onChangeVehicle}
                        error={showErrors.registrationNumber.length > 0}
                        helperText={showErrors.registrationNumber.length > 0 ? showErrors.registrationNumber : ''}
                    />
                    <FormControl fullWidth style={{ marginRight: '10px' }} error={showErrors.vehicleType.length > 0}>
                        <InputLabel id="vehicle-type-label">Vehicle Type</InputLabel>
                        <Select
                            labelId="vehicle-type-label"
                            id="vehicle-type-select"
                            value={showVehicle.vehicleType}
                            label="Vehicle Type"
                            name="vehicleType"
                            onChange={onChangeVehicle}
                        >
                            {VEHICLE_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Make"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="make"
                        value={showVehicle.make}
                        onChange={onChangeVehicle}
                        error={showErrors.make.length > 0}
                        helperText={showErrors.make.length > 0 ? showErrors.make : ''}
                    />
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Model"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="model"
                        value={showVehicle.model}
                        onChange={onChangeVehicle}
                        error={showErrors.model.length > 0}
                        helperText={showErrors.model.length > 0 ? showErrors.model : ''}
                    />
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Color"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="color"
                        value={showVehicle.color}
                        onChange={onChangeVehicle}
                    />
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Capacity"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="capacityKg"
                        type="number"
                        value={showVehicle.capacityKg}
                        onChange={onChangeVehicle}
                        error={showErrors.capacityKg.length > 0}
                        helperText={showErrors.capacityKg.length > 0 ? showErrors.capacityKg : ''}
                        InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                    />
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Capacity Volume"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="capacityVolume"
                        type="number"
                        value={showVehicle.capacityVolume}
                        onChange={onChangeVehicle}
                        InputProps={{ endAdornment: <InputAdornment position="end">cu.ft</InputAdornment> }}
                    />
                    <FormControl fullWidth style={{ marginRight: '10px' }}>
                        <InputLabel id="vehicle-status-label">Status</InputLabel>
                        <Select
                            labelId="vehicle-status-label"
                            id="vehicle-status-select"
                            value={showVehicle.status}
                            label="Status"
                            name="status"
                            onChange={onChangeVehicle}
                        >
                            {VEHICLE_STATUS.map((status) => (
                                <MenuItem key={status.value} value={status.value}>
                                    {status.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <Autocomplete
                        disablePortal
                        id="driver-autocomplete"
                        options={drivers}
                        fullWidth
                        style={{ marginRight: '10px' }}
                        value={selectedDriver}
                        onChange={onChangeDriver}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                        renderInput={(params) => <TextField {...params} label="Driver" />}
                    />
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Insurance Expiry"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="insuranceExpiry"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={showVehicle.insuranceExpiry}
                        onChange={onChangeVehicle}
                    />
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Fitness Expiry"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="fitnessExpiry"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={showVehicle.fitnessExpiry}
                        onChange={onChangeVehicle}
                    />
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Pollution Expiry"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="pollutionExpiry"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={showVehicle.pollutionExpiry}
                        onChange={onChangeVehicle}
                    />
                </Stack>

                <Stack direction="row" justifyContent="space-between" sx={{ my: 2 }}>
                    <TextField
                        id="outlined-basic"
                        fullWidth
                        label="Notes"
                        variant="outlined"
                        style={{ marginRight: '10px' }}
                        name="notes"
                        multiline
                        rows={3}
                        value={showVehicle.notes}
                        onChange={onChangeVehicle}
                    />
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent={'center'} mt={5} mb={5}>
                    {showEditButton ? (
                        <Button variant="contained" style={{ marginRight: '10px' }} onClick={onClickEdit}>
                            Edit
                        </Button>
                    ) : (
                        <Button variant="contained" style={{ marginRight: '10px' }} onClick={onClickSave}>
                            Add
                        </Button>
                    )}
                    <Button variant="contained" color="error" onClick={onClickCancel}>
                        Cancel
                    </Button>
                </Stack>
            </Container>
        </>
    );
}
